import { Check } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export type WizardStep = "source" | "input" | "result";

const STEPS: ReadonlyArray<{ key: WizardStep; label: string }> = [
  { key: "source", label: "Source" },
  { key: "input", label: "Configure" },
  { key: "result", label: "Result" },
];

interface Props {
  active: WizardStep;
  /** When true, the active step also renders as "done" (used on the
   *  Result step once the import has finished). */
  complete?: boolean;
}

/**
 * Status line for the AddUsersDialog. Three discrete steps because the
 * import flow has only one branching point per source. Past steps render
 * with a check; future steps render dim. The active step shows "current"
 * unless `complete` is set, in which case it ticks as done too.
 */
export function AddUsersStepper({ active, complete }: Props) {
  const activeIdx = STEPS.findIndex((s) => s.key === active);
  return (
    <ol className="flex items-center justify-center gap-2" aria-label="Progress">
      {STEPS.map((s, i) => {
        const status: "done" | "current" | "todo" =
          i < activeIdx || (i === activeIdx && complete)
            ? "done"
            : i === activeIdx
              ? "current"
              : "todo";
        return (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={cn(
                "flex size-6 items-center justify-center rounded-full text-2xs font-semibold transition-colors",
                status === "done" && "bg-pulse text-white",
                status === "current" &&
                  "border-2 border-pulse bg-pulse-soft text-text-brand-strong",
                status === "todo" && "border border-border bg-surface-subtle text-text-tertiary",
              )}
              aria-current={status === "current" ? "step" : undefined}
            >
              {status === "done" ? <Check className="size-3" aria-hidden="true" /> : i + 1}
            </span>
            <span
              className={cn(
                "text-xs font-medium",
                status === "current"
                  ? "text-text-primary"
                  : status === "done"
                    ? "text-text-secondary"
                    : "text-text-tertiary",
              )}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 ? (
              <span
                aria-hidden="true"
                className={cn(
                  "h-px w-8 transition-colors",
                  status === "done" ? "bg-pulse" : "bg-border",
                )}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
